// src/views/Admin/AllInvoices.jsx
import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import './AllInvoices.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';

const AllInvoices = () => {
  const [allInvoices, setAllInvoices] = useState([]);
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [selectedTo, setSelectedTo] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllInvoices = async () => {
      try {
        const clientsSnap = await getDocs(collection(db, "clients"));
        const invoicesMap = new Map();
  
        for (const clientDoc of clientsSnap.docs) {
          const clientId = clientDoc.id;
          const clientData = clientDoc.data();
          const invoicesSnap = await getDocs(collection(db, "clients", clientId, "invoices"));
  
          invoicesSnap.forEach((invDoc) => {
            const data = invDoc.data();
            const createdAt = data.createdAt?.toDate?.() || new Date(0);
  
            // 🔹 Clave única: cliente + total
            const key = `${clientData.email || "noemail"}-${data.total || 0}`;
  
            // 🔸 Si ya hay una factura con esta clave, nos quedamos con la más nueva
            if (!invoicesMap.has(key) || createdAt > invoicesMap.get(key).createdAt) {
              invoicesMap.set(key, {
                id: invDoc.id,
                clientId,
                clientName: clientData.businessName || "—",
                clientEmail: clientData.email || "—",
                ...data,
                createdAt,
              });
            }
          });
        }
  
        // 🔹 Pasamos el map a array limpio
        const all = Array.from(invoicesMap.values());
        setAllInvoices(all);
      } catch (err) {
        console.error("❌ Error cargando facturas globales:", err);
      }
    };
  
    fetchAllInvoices();
  }, []);
  

  const filtered = allInvoices.filter(inv => {
    const date = inv.createdAt;
    if (!date) return false;

    const from = selectedFrom ? new Date(selectedFrom.getFullYear(), selectedFrom.getMonth(), 1) : null;
    const to = selectedTo ? new Date(selectedTo.getFullYear(), selectedTo.getMonth() + 1, 0) : null;

    return (!from || date >= from) && (!to || date <= to);
  });

  const handleOpenInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowModal(true);
  };

  const handleNavigateClient = (invoice) => {
    if (!invoice?.clientId && !invoice?.clientEmail) return;
    const refId = invoice.clientId || encodeURIComponent((invoice.clientEmail || '').toLowerCase());
    navigate(`/admin/clients/${refId}/invoices`);
  };

  return (
    <div className="all-invoices-container">
      <h2>Facturas Globales</h2>

      <div className="filters">
        <label>Desde:
          <DatePicker
            selected={selectedFrom}
            onChange={date => setSelectedFrom(date)}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            placeholderText="Mes/Año inicial"
          />
        </label>
        <label>Hasta:
          <DatePicker
            selected={selectedTo}
            onChange={date => setSelectedTo(date)}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            placeholderText="Mes/Año final"
          />
        </label>
      </div>

      <div className="table-wrapper">
        <table className="invoices-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Email</th>
              <th>Total</th>
              <th>Depósito</th>
              <th>Pagos</th>
              <th>Saldo</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(inv => {
              const payments = (inv.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
              const remaining = (inv.total || 0) - (inv.deposit || 0) - (inv.discount || 0) - payments;
              return (
                <tr
                  key={inv.id}
                  className="clickable-row"
                  onClick={() => handleOpenInvoice(inv)}
                >
                  <td>{inv.createdAt?.toLocaleDateString() || '—'}</td>
                  <td>{inv.clientName}</td>
                  <td>{inv.clientEmail}</td>
                  <td>${parseFloat(inv.total || 0).toFixed(2)}</td>
                  <td>${parseFloat(inv.deposit || 0).toFixed(2)}</td>
                  <td>${payments.toFixed(2)}</td>
                  <td>${remaining.toFixed(2)}</td>
                  <td>{inv.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && selectedInvoice && (
        <div className="modal-overlay">
          <div className="modal-content max-w-3xl">
            <div className="modal-header">
              <h3>Factura #{selectedInvoice.id.slice(0, 8)}</h3>
              <button onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body space-y-3">
              <p><strong>Cliente:</strong> {selectedInvoice.clientName}</p>
              <p><strong>Email:</strong> {selectedInvoice.clientEmail}</p>
              <p><strong>Fecha:</strong> {selectedInvoice.createdAt?.toLocaleDateString?.('es-PR') || '—'}</p>
              <p><strong>Total:</strong> ${parseFloat(selectedInvoice.total || 0).toFixed(2)}</p>
              <p><strong>Depósito:</strong> ${parseFloat(selectedInvoice.deposit || 0).toFixed(2)}</p>
              <p><strong>Pagos:</strong> ${(selectedInvoice.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}</p>
              <p><strong>Saldo:</strong> ${((selectedInvoice.total || 0) - (selectedInvoice.deposit || 0) - (selectedInvoice.discount || 0) - ((selectedInvoice.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))).toFixed(2)}</p>
              <p><strong>Estado:</strong> {selectedInvoice.status || 'pendiente'}</p>

              <div>
                <h4 className="font-semibold mt-4 mb-2">Ítems</h4>
                <ul className="proposal-items">
                  {(selectedInvoice.items || []).map((item, idx) => (
                    <li key={idx}>
                      <p><strong>{item.name || `Ítem ${idx + 1}`}</strong></p>
                      <p className="text-sm text-gray-600">{item.description || '—'}</p>
                      <p className="text-sm">${parseFloat(item.price || 0).toFixed(2)} x {item.quantity || 1}</p>
                    </li>
                  ))}
                  {(!selectedInvoice.items || selectedInvoice.items.length === 0) && (
                    <li className="text-sm text-gray-500">Sin ítems registrados.</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => handleNavigateClient(selectedInvoice)} className="btn-primary">
                Ir al cliente
              </button>
              <button onClick={() => setShowModal(false)} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllInvoices;
